import React, { useCallback, useEffect, useState } from 'react'
import {
  CVRParticipant,
  FoundingParentManagementSnapshot,
  OwnershipGraphData,
} from '../../types'
import CompanyOwnershipStrip from './CompanyOwnershipStrip'
import OwnershipGraph from './OwnershipGraph'
import OwnershipOverTime from './OwnershipOverTime'
import {
  getOwnershipGraphAsOf,
  getOwnershipTimeline,
  type OwnershipTimelineEvent,
} from '../../services/apiClient'

interface EjerskabOverviewProps {
  founders: CVRParticipant[]
  foundingManagementAtParents?: FoundingParentManagementSnapshot[]
  legalOwners: CVRParticipant[]
  beneficialOwners: CVRParticipant[]
  /** Flat list of all participants on the root company (for graph node panel). */
  rootParticipants?: CVRParticipant[]
  /** Ownership graph data from the /graph endpoint. */
  graph?: OwnershipGraphData | null
  /** True while the graph is still being fetched. */
  graphLoading?: boolean
  /** Founding date of the company (startdato from company details). */
  startdato?: string | null
  /** Company CVR — enables »Ejerskab over tid«. */
  cvrNumber?: string
}

/** A historical graph is read-only: no fold-out from today's data. */
function asReadOnly(data: OwnershipGraphData, asOf: string): OwnershipGraphData {
  return {
    ...data,
    asOf,
    hasDownstream: false,
    nodes: data.nodes.map((n) => ({ ...n, truncated: false })),
  }
}

/** Shape key under the graph — same shapes as the nodes themselves. */
const GraphLegend: React.FC<{ historic?: boolean }> = ({ historic = false }) => (
  <ul
    className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-ink-500"
    aria-label="Signaturforklaring"
  >
    <li className="inline-flex items-center gap-1.5">
      <span className="h-3 w-6 rounded-full bg-ink" aria-hidden />
      Reel ejer
    </li>
    <li className="inline-flex items-center gap-1.5">
      <span className="h-3 w-6 rounded-full border border-rule-node bg-paper" aria-hidden />
      Person
    </li>
    <li className="inline-flex items-center gap-1.5">
      <span className="h-3 w-6 rounded-[3px] border border-rule-node bg-paper" aria-hidden />
      Dansk selskab
    </li>
    <li className="inline-flex items-center gap-1.5">
      <span className="h-3 w-6 rounded-[3px] border border-signal bg-signal-50" aria-hidden />
      Udenlandsk, med flag
    </li>
    <li className="inline-flex items-center gap-1.5">
      <svg width="10" height="13" viewBox="0 0 12 16" aria-hidden>
        <path d="M11 15 6 12 1 15V2a1.5 1.5 0 0 1 1.5-1.5h7A1.5 1.5 0 0 1 11 2z" fill="#C93A22" />
      </svg>
      På din watchlist
    </li>
    <li className="inline-flex items-center gap-1.5">
      <span className="text-ink-400" aria-hidden>…</span>
      Flere led, kan foldes ud
    </li>
    {historic && (
      <li className="inline-flex items-center gap-1.5">
        <svg width="24" height="4" aria-hidden>
          <line x1="0" y1="2" x2="24" y2="2" stroke="#AEB5BF" strokeWidth="1.5" strokeDasharray="5 4" />
        </svg>
        <span>
          Stiplet, <span className="line-through">overstreget</span>: ejer ikke i dag
        </span>
      </li>
    )}
  </ul>
)

/**
 * Ejerskab-fanen:
 *
 *   1. Kompakt overblik — reelle ejere, ejere, stiftere (à la person Overblik)
 *   2. Ejerskabsstruktur — SVG-graf
 */
const EjerskabOverview: React.FC<EjerskabOverviewProps> = ({
  founders,
  foundingManagementAtParents = [],
  legalOwners,
  beneficialOwners,
  rootParticipants = [],
  graph = null,
  graphLoading = false,
  startdato = null,
  cvrNumber,
}) => {
  // No owners above and no subsidiaries: no graph, no explanation (keep it simple)
  // — unless there were owners before, then »Historisk ejerskab« still has a story.
  const hasGraph = !!graph && (graph.edges.length > 0 || !!graph.hasDownstream)
  const hadOwners = [...legalOwners, ...beneficialOwners].some(
    (p) =>
      p.role.isActive === false ||
      (p.role.organizationType === 'EJERREGISTER' && p.role.legalRegisterShowInPrimaryList === false)
  )
  const showGraphSection = graphLoading || hasGraph || (hadOwners && !!cvrNumber)

  const [timeOpen, setTimeOpen] = useState(false)
  const [events, setEvents] = useState<OwnershipTimelineEvent[] | null>(null)
  const [eventsLoading, setEventsLoading] = useState(false)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [asOfGraph, setAsOfGraph] = useState<OwnershipGraphData | null>(null)
  const [asOfLoading, setAsOfLoading] = useState(false)
  const [asOfError, setAsOfError] = useState<string | null>(null)
  const [asOfAttempt, setAsOfAttempt] = useState(0)

  useEffect(() => {
    if (!timeOpen || !cvrNumber || events !== null) return
    let cancelled = false
    setEventsLoading(true)
    getOwnershipTimeline(cvrNumber)
      .then((ev) => !cancelled && setEvents(ev))
      .catch(() => !cancelled && setEvents([]))
      .finally(() => !cancelled && setEventsLoading(false))
    return () => {
      cancelled = true
    }
  }, [timeOpen, cvrNumber, events])

  useEffect(() => {
    if (!asOf || !cvrNumber) {
      setAsOfGraph(null)
      setAsOfError(null)
      return
    }
    // The graph on screen stays (dimmed) until the new date's graph is here.
    let cancelled = false
    setAsOfLoading(true)
    setAsOfError(null)
    getOwnershipGraphAsOf(cvrNumber, asOf)
      .then((data) => !cancelled && setAsOfGraph(asReadOnly(data, asOf)))
      .catch((err: unknown) => {
        if (cancelled) return
        setAsOfGraph(null)
        setAsOfError(err instanceof Error ? err.message : 'Fejl')
      })
      .finally(() => !cancelled && setAsOfLoading(false))
    return () => {
      cancelled = true
    }
  }, [asOf, cvrNumber, asOfAttempt])

  const handleAsOf = useCallback((next: string | null) => setAsOf(next), [])
  const toggleTime = () => {
    setTimeOpen((open) => {
      if (open) setAsOf(null)
      return !open
    })
  }
  const retryAsOf = useCallback(() => setAsOfAttempt((n) => n + 1), [])
  // An owner who came in after the date: today's graph, with that owner selected.
  const [focusNodeId, setFocusNodeId] = useState<string | null>(null)
  const showToday = useCallback((nodeId: string) => {
    setAsOf(null)
    setFocusNodeId(nodeId)
  }, [])
  const focusDone = useCallback(() => setFocusNodeId(null), [])
  // Picking the first date: today's graph stays up, dimmed, until the old one arrives.
  const shownGraph = asOf ? (asOfGraph ?? (asOfLoading ? graph : null)) : graph

  return (
    <div className="space-y-7">
      <CompanyOwnershipStrip
        beneficialOwners={beneficialOwners}
        legalOwners={legalOwners}
        founders={founders}
        foundingManagementAtParents={foundingManagementAtParents}
        startdato={startdato}
      />

      {showGraphSection && (
        <section aria-labelledby="ejerskab-graf-label">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
            <div className="min-w-0">
              <h2 id="ejerskab-graf-label" className="text-[17px] font-bold text-ink">
                Ejerskabsstruktur
              </h2>
              <p className="mt-0.5 text-sm text-ink-500">
                Ejere over selskabet, datterselskaber under. Hold musen over et selskab eller en
                person for at se kæden og andelene. Klik for detaljer og kæden i ord.
                {cvrNumber && ' Uret i grafen viser ejerskabet på en tidligere dato.'}
              </p>
            </div>
          </div>
          <OwnershipGraph
            data={shownGraph}
            loading={asOf ? asOfLoading && !shownGraph : graphLoading}
            rootParticipants={rootParticipants}
            beneficialOwners={beneficialOwners}
            asOf={asOf}
            refreshing={!!asOf && asOfLoading}
            error={asOf ? asOfError : null}
            onRetry={retryAsOf}
            today={graph}
            focusNodeId={focusNodeId}
            onFocusDone={focusDone}
            onShowToday={showToday}
            onToggleHistory={cvrNumber ? toggleTime : undefined}
            historyOpen={timeOpen}
            historyPanel={
              <OwnershipOverTime
                events={events ?? []}
                loading={eventsLoading || asOfLoading}
                startdato={startdato}
                asOf={asOf}
                onChange={handleAsOf}
                onClose={toggleTime}
              />
            }
          />
          <GraphLegend historic={!!asOf} />
        </section>
      )}
    </div>
  )
}

export default EjerskabOverview
